import React, { forwardRef, useRef } from 'react';
import { MenuList, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { Popover, MenuItem } from './styled';
import { IconButton } from '../icon-button';
import { Button } from '../button';
import { cloudPlexUseTheme } from '../theme';
import { Tooltip } from '../tooltip';
import { alpha } from '@mui/material/styles';
import { MenuProps } from 'types';

const getTextWidth = (title: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = '14px Noto Sans KR';
    const width = context.measureText(title).width;
    return width;
  }
  return 0;
};

export const Menu = forwardRef<HTMLButtonElement, MenuProps>(
  (
    {
      target,
      menuWidth,
      menuMargin,
      placement,
      type,
      startIcon,
      endIcon,
      endOpenIcon,
      label,
      color,
      size,
      variant,
      list,
      listSize,
      actions,
      divider,
      preventPropagation = true,
      useTooltip = false,
      ...restProps
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const testRef = useRef<HTMLLIElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (preventPropagation) event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };
    const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (preventPropagation) event.stopPropagation();
      setAnchorEl(null);
    };

    const theme = cloudPlexUseTheme();
    return (
      <>
        {type === 'icon' ? (
          <IconButton
            ref={ref}
            id='menu-button'
            aria-controls={open ? 'menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            color={color}
            size={size}
            circled
            active={open}
            {...restProps}
          >
            {label}
          </IconButton>
        ) : (
          <Button
            ref={ref}
            id='menu-button'
            aria-controls={open ? 'menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            color={color}
            size={size}
            variant={variant}
            startIcon={startIcon}
            endIcon={open ? endOpenIcon : endIcon}
            {...restProps}
          >
            {label}
          </Button>
        )}
        <Popover
          id='menu-popover'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: placement ?? 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: placement ?? 'left'
          }}
          PaperProps={{
            style: {
              width: `${menuWidth}px`,
              margin: `${menuMargin}px`
            }
          }}
        >
          <MenuList id='menu-list'>
            {list
              ?.filter((item) => !item.disabled)
              .map((item, index) => {
                const width = getTextWidth(item.label);
                return [
                  item.divider && <Divider />,
                  <Tooltip title={useTooltip && width >= 274 ? item.label : ''}>
                    <MenuItem
                      ref={testRef}
                      id='menu-item'
                      key={item.label}
                      disabled={item.buttonDisabled}
                      disableGutters
                      onClick={(e: any) => {
                        if (item.onClick) item.onClick(item.value);
                        if (item.clickClose) handleClose(e);
                      }}
                      sx={{
                        height: listSize === 'small' ? '32px' : '40px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.secondary[800], 0.08)
                        },
                        '&:active, &.Mui-selected': {
                          backgroundColor: alpha('#c9d4f4', 0.04)
                        }
                      }}
                    >
                      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                      {item.control && <>{item.control}</>}
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ component: 'span' }}
                        sx={{
                          '& .MuiTypography-body1': { fontSize: '14px', fontWeight: 'normal' }
                        }}
                      />
                    </MenuItem>
                  </Tooltip>
                ];
              })}
          </MenuList>
          {actions && (
            <div id={'menu-item-actions'} role={'menuitem'}>
              {actions}
            </div>
          )}
        </Popover>
      </>
    );
  }
);
export default Menu;
